import { CheckCircle, CheckCircleOutline } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { queryClient } from '../../../../../../services/react-query/queryClient';
import { queryKeys } from '../../../../../../services/react-query/queryKeys';
import { useGetTaskById, useUpdateTask } from '../../../../../../services/react-query/tasks';
import { useUpdateTaskStep } from '../../../../../../services/react-query/tasks/useUpdateTaskStep';
import { Step, StepAction } from '../../../../../../types/Step';
import { TaskStatus } from '../../../../../../types/Task';
import { humanizeDate, humanizeTimestamp } from '../../../../../../utility/dateUtils';
import { taskBaseColor } from '../../../../../../utility/task';
import TaskDuePill from '../../../../../tasks/TaskDuePill';
import StatusField from '../../../../../tasks/StatusField';
import TaskStatusPill from '../../../../../tasks/TaskStatusPill';
import DrawerSection from '../../DrawerSection';
import InfoRow from '../../InfoRow';
import ModuleDrawerHeader from '../../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../../moduleDrawerStyles';
import CustomFieldDisplay from '../CustomFieldDisplay';
import { getStepButtonStyles, taskDrawerViewStyles } from './taskDrawerViewStyles';
import { COMPLETE_COLOR } from '../../../../../steps/StepItem';

const IN_PROGRESS_COLOR = taskBaseColor(TaskStatus.IN_PROGRESS);

const CenterSpinner = () => (
  <Box sx={taskDrawerViewStyles.spinner}>
    <CircularProgress size={20} />
  </Box>
);

const TaskDrawerView: React.FC<{ uuid: string }> = ({ uuid }) => {
  const { data: task, isLoading } = useGetTaskById({ uuid });
  const { mutate: updateStep } = useUpdateTaskStep();

  const { mutate: updateTask } = useUpdateTask({
    uuid,
    onSuccessCallback: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.allTasks] });
      setIsUpdating(false);
    },
  });

  const [steps, setSteps] = useState<Step[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loadingStep, setLoadingStep] = useState<{ uuid: string; action: StepAction } | null>(null);

  useEffect(() => {
    setSteps(task?.steps ?? []);
  }, [task]);

  const sortedSteps = useMemo(() => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [steps]);

  const handleStatusChange = (status: TaskStatus | null) => {
    if (!(task && status)) return;
    setIsUpdating(true);
    updateTask({ ...task, status });
  };

  const handleStepToggle = useCallback(
    (step: Step, action: StepAction) => {
      const started = !step.started_at;
      const completed = !step.completed_at;

      setLoadingStep({ uuid: step.uuid, action });

      updateStep(
        {
          uuid: step.uuid,
          step_action: action,
          started: action === StepAction.START ? started : false,
          completed: action === StepAction.COMPLETE ? completed : false,
        },
        {
          onSuccess: (updatedStep: Step) => {
            setSteps((prev) => prev.map((s) => (s.uuid === updatedStep.uuid ? updatedStep : s)));
            queryClient.invalidateQueries({ queryKey: [queryKeys.allTasks] });
          },
          onSettled: () => setLoadingStep(null),
        }
      );
    },
    [updateStep]
  );

  if (isLoading) return <CenterSpinner />;

  if (!task) {
    return (
      <Typography variant="body2" color="error">
        Task not found
      </Typography>
    );
  }

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(task.name)}</ModuleDrawerHeader>

      <Box sx={moduleDrawerStyles.content}>
        <Box sx={taskDrawerViewStyles.sectionWrapper}>
          <DrawerSection title="Task Information">
            <InfoRow label="Name" value={task.name} />
            <InfoRow label="Account" value={task.account_name} />
            <InfoRow label="Event" value={task.event_display_name} />
            <InfoRow label="Task Type" value={task.type_value} />
            <InfoRow label="Assigned To" value={task.employee_display_name} />
            <InfoRow label="Start Date" value={humanizeDate(task.start_date)} />
            <InfoRow label="Due Date" value={humanizeDate(task.due_date)} rightNode={<TaskDuePill task={task} />} />

            <InfoRow
              label="Status"
              rightNode={
                <StatusField
                  fullWidth
                  value={task.status}
                  options={_.values(TaskStatus)}
                  disabled={isUpdating}
                  getBackgroundColor={taskBaseColor}
                  renderOptionNode={(status) => <TaskStatusPill status={status} />}
                  onChange={(_, value) => handleStatusChange(value)}
                />
              }
            />
          </DrawerSection>
          {/* Custom Fields */}
          {task.fields?.length > 0 && (
            <DrawerSection title="More Details">
              {_.map(task.fields, (field) => (
                <CustomFieldDisplay key={field.label} field={field} />
              ))}
            </DrawerSection>
          )}

          <DrawerSection title="Steps">
            <TableContainer sx={taskDrawerViewStyles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Step Name</TableCell>
                    <TableCell sx={{ width: 240 }}>Start</TableCell>
                    <TableCell sx={{ width: 240 }}>Complete</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedSteps.map((step) => {
                    const started = Boolean(step.started_at);
                    const completed = Boolean(step.completed_at);

                    const isStartLoading = loadingStep?.uuid === step.uuid && loadingStep?.action === StepAction.START;

                    const isCompleteLoading = loadingStep?.uuid === step.uuid && loadingStep?.action === StepAction.COMPLETE;

                    return (
                      <TableRow key={step.uuid}>
                        <TableCell>{step.name}</TableCell>

                        {/* START */}
                        <TableCell>
                          <Box sx={taskDrawerViewStyles.stepCellContent}>
                            {!completed && (
                              <IconButton
                                disabled={isStartLoading}
                                onClick={() => handleStepToggle(step, StepAction.START)}
                                sx={getStepButtonStyles({
                                  active: started,
                                  activeColor: IN_PROGRESS_COLOR,
                                })}
                              >
                                {isStartLoading ? (
                                  <CircularProgress size={16} />
                                ) : started ? (
                                  <CheckCircle sx={{ color: IN_PROGRESS_COLOR }} />
                                ) : (
                                  <CheckCircleOutline sx={{ color: '#9AA0A6' }} />
                                )}
                              </IconButton>
                            )}

                            <Box sx={taskDrawerViewStyles.stepMetaBox}>
                              {step.started_by_name && <Typography variant="caption">{step.started_by_name}</Typography>}
                              {step.started_at && (
                                <Typography variant="caption" color="text.secondary">
                                  {humanizeTimestamp(step.started_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* COMPLETE */}
                        <TableCell>
                          <Box sx={taskDrawerViewStyles.stepCellContent}>
                            <IconButton
                              disabled={isCompleteLoading}
                              onClick={() => handleStepToggle(step, StepAction.COMPLETE)}
                              sx={getStepButtonStyles({
                                active: completed,
                                activeColor: COMPLETE_COLOR,
                                activeBg: '#E8F5E9',
                              })}
                            >
                              {isCompleteLoading ? (
                                <CircularProgress size={16} />
                              ) : completed ? (
                                <CheckCircle sx={{ color: COMPLETE_COLOR }} />
                              ) : (
                                <CheckCircleOutline sx={{ color: '#9AA0A6' }} />
                              )}
                            </IconButton>

                            <Box sx={taskDrawerViewStyles.stepMetaBox}>
                              {step.completed_by_name && <Typography variant="caption">{step.completed_by_name}</Typography>}
                              {step.completed_at && (
                                <Typography variant="caption" color="text.secondary">
                                  {humanizeTimestamp(step.completed_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {sortedSteps.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary" sx={taskDrawerViewStyles.emptyState}>
                          No steps available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default TaskDrawerView;
